/**
 * PDF Service API Client
 * Separate client for the PDF generation microservice
 */

import axios, { AxiosInstance } from 'axios';

interface PDFGenerationData {
  candidate_id: string;
  requirement_id: string;
  include_sensitive_data?: boolean;
  report_format?: string;
}

interface PDFGenerationResponse {
  success: boolean;
  download_url?: string;
  filename?: string;
  s3_key?: string;
  generated_at?: string;
  message?: string;
}

class PDFServiceClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Get PDF service URL from environment variables
    // Check for production mode using multiple methods
    let isProd = false;
    
    if (typeof window !== 'undefined') {
      // Check hostname for production domain (multiple possible domains)
      const hostname = window.location.hostname;
      
      // Explicitly check for dev environment FIRST
      const isDevEnvironment = hostname.includes('recruitedgedev') || 
                               hostname.includes('localhost') || 
                               hostname.includes('127.0.0.1');
      
      // Only consider production if NOT dev environment
      isProd = !isDevEnvironment && (
        hostname.includes('recruitedge.otomeytai.com') || 
        hostname.includes('recruitedge.com') ||
        hostname.includes('otomeytai.com')
      );
      
      // Also check for Vite environment variables if available
      try {
        const env = (window as any).import?.meta?.env;
        if (env?.PROD) {
          isProd = true;
        }
        // Check for NODE_ENV or MODE environment variables
        if (env?.MODE === 'production' || env?.NODE_ENV === 'production') {
          isProd = true;
        }
        // Check for custom build mode variable
        if (env?.VITE_BUILD_MODE === 'production') {
          isProd = true;
        }
      } catch (e) {
        // Ignore if import.meta is not available
      }
      
      // Check for explicit environment override in localStorage (for debugging)
      const envOverride = localStorage.getItem('pdf-service-env');
      if (envOverride === 'prod') {
        isProd = true;
        console.log('🔧 PDF Service: Forced to PRODUCTION via localStorage override');
      } else if (envOverride === 'dev') {
        isProd = false;
        console.log('🔧 PDF Service: Forced to DEVELOPMENT via localStorage override');
      }
    }
    
    // Use environment variables for PDF service URLs
    const devPdfUrl = import.meta.env.VITE_PDF_SERVICE_DEV_URL || 'https://spfqk0wmcc.execute-api.ap-southeast-1.amazonaws.com';
    const prodPdfUrl = import.meta.env.VITE_PDF_SERVICE_PROD_URL || 'https://27ryvtfiek.execute-api.ap-southeast-1.amazonaws.com';
    
    // Additional fallback: Check if we're in a production build by looking at the build artifacts
    // Production builds typically have minified code and different chunk names
    const isProductionBuild = typeof window !== 'undefined' && 
      (window.location.pathname.includes('assets/') || 
       document.querySelector('script[src*="index-"]')?.getAttribute('src')?.includes('index-'));
    
    // Final environment determination
    const finalIsProd = isProd || isProductionBuild;
    
    // Use the deployed PDF service URL based on environment
    if (finalIsProd) {
      // Production PDF service URL
      this.baseURL = prodPdfUrl;
    } else {
      // Development PDF service URL (default for localhost and dev domains)
      this.baseURL = devPdfUrl;
    }
    
    console.log('🔧 PDF Service: Using', finalIsProd ? 'PRODUCTION' : 'DEVELOPMENT', 'PDF service:', this.baseURL);
    console.log('🔧 PDF Service: Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'server-side');
    console.log('🔧 PDF Service: Environment override:', typeof window !== 'undefined' ? localStorage.getItem('pdf-service-env') : 'N/A');
    console.log('🔧 PDF Service: Vite env:', typeof window !== 'undefined' ? (window as any).import?.meta?.env : 'N/A');
    console.log('🔧 PDF Service: Dev URL:', devPdfUrl);
    console.log('🔧 PDF Service: Prod URL:', prodPdfUrl);
    console.log('🔧 PDF Service: isProd:', isProd, 'isProductionBuild:', isProductionBuild, 'finalIsProd:', finalIsProd);

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 300000, // 5 minutes timeout for PDF generation
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('PDF Service API Error:', error);
        
        if (error.response?.status === 401) {
          // Token expired, redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
    );
  }

  async generateJDCAnalysisPDF(data: PDFGenerationData): Promise<PDFGenerationResponse> {
    try {
      console.log('🔄 PDF Service: Generating JD CV analysis PDF...', data);
      
      // For now, we'll use a temporary endpoint until the PDF service is deployed
      // This will be updated to use the actual PDF service endpoint
      const response = await this.client.post<PDFGenerationResponse>(
        '/api/v1/generate-jd-cv-analysis', 
        data
      );
      
      console.log('✅ PDF Service: PDF generation successful', response.data);
      return response.data;
      
    } catch (error: any) {
      console.error('❌ PDF Service: PDF generation failed', error);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        throw new Error('Candidate or requirement not found');
      } else if (error.response?.status === 403) {
        throw new Error('Insufficient permissions to generate PDF reports');
      } else if (error.response?.status === 500) {
        throw new Error('PDF generation service is temporarily unavailable');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('PDF generation timed out. Please try again.');
      } else {
        throw new Error(error.response?.data?.detail || 'Failed to generate PDF report');
      }
    }
  }

  async healthCheck(): Promise<{ status: string; service: string }> {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      console.error('PDF Service health check failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const pdfServiceClient = new PDFServiceClient();
export type { PDFGenerationData, PDFGenerationResponse };
